<template>
  <Teleport to="body">
    <div class="eus-modal eh-popup-overlay">
      <div v-if="!selectedRecord" class="eus-card eh-popup-panel">
        <div class="eus-head">
          <h2>Edit Users</h2>
          <button type="button" class="eus-close" aria-label="Close" @click="handleClose">&times;</button>
        </div>
        <p class="eus-hint">Search by name, username, or email to find someone in your organization.</p>
        <div class="eus-search-wrap">
          <input
            type="text"
            v-model="query"
            class="eus-search-input"
            placeholder="Search for a user..."
            autofocus
            @input="onQueryInput"
          />
          <ul v-if="query.trim()" class="eus-search-suggestions">
            <li v-if="searching" class="eus-empty-sm">Searching...</li>
            <template v-else>
              <li v-for="r in results" :key="r.uniqueID || r.email">
                <button type="button" class="eus-suggestion-btn" @click="pickResult(r)">
                  <span class="eus-avatar">{{ initials(r.firstname, r.lastname) }}</span>
                  <span class="eus-suggestion-text">
                    <span class="eus-suggestion-name">{{ r.firstname }} {{ r.lastname }}</span>
                    <span class="eus-suggestion-sub">{{ r.accountType || "Account" }} &middot; {{ r.email }}</span>
                  </span>
                </button>
              </li>
              <li v-if="results.length === 0" class="eus-empty-sm">No matches found</li>
            </template>
          </ul>
        </div>
      </div>

      <div v-else-if="!editChoiceMade && !hasRoleRecord" class="eus-card eus-choice-card eh-popup-panel">
        <div class="eus-head">
          <h2>{{ selectedRecord.firstname }} {{ selectedRecord.lastname }}</h2>
          <button type="button" class="eus-close" aria-label="Close" @click="handleClose">&times;</button>
        </div>
        <p class="eus-hint">
          This person is currently only set up as an <strong>{{ selectedRecord.accountType || "Administrator" }}</strong><template v-if="!selectedRecord.hasProfessionalLicense"> (no professional license on file)</template>.
          What would you like to do?
        </p>
        <div class="eus-choice-buttons">
          <button type="button" class="btn btn-secondary" @click="chooseEditDetails">
            Edit Account Details
          </button>
          <button type="button" class="btn btn-primary" @click="chooseAddNewType">
            Add a New Account Type
          </button>
        </div>
      </div>

      <add-user-form
        v-else
        edit-mode
        :edit-record="selectedRecord"
        :edit-keep-current-type="editKeepCurrentType"
        @close="handleClose"
        @saved="onSaved"
      />
    </div>
  </Teleport>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { CURR_API } from "@/constants/apis";
import AddUserForm from "@/components/private-layouts/welcome/AddUserForm.vue";

export default {
  name: "EditUserSearch",
  components: {
    AddUserForm,
  },
  props: {
    /** When set (e.g. from Administration Add/Edit chooser), skip search and open this record. */
    initialRecord: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      query: "",
      results: [],
      searching: false,
      debounceTimer: null,
      selectedRecord: this.initialRecord || null,
      editChoiceMade: false,
      editKeepCurrentType: false,
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    hasRoleRecord() {
      const foundIn = (this.selectedRecord && this.selectedRecord.foundIn) || [];
      return foundIn.includes("providers") || foundIn.includes("nurses") || foundIn.includes("patients");
    },
  },
  mounted() {
    // Preselected from Administration chooser with an existing role → skip search/choice UI.
    if (this.initialRecord && this.hasRoleRecord) {
      this.editKeepCurrentType = true;
      this.editChoiceMade = true;
    }
  },
  methods: {
    initials(first, last) {
      const f = (first || "").trim().charAt(0);
      const l = (last || "").trim().charAt(0);
      return (f + l).toUpperCase() || "?";
    },
    onQueryInput() {
      clearTimeout(this.debounceTimer);
      const term = this.query.trim();
      if (!term) {
        this.results = [];
        return;
      }
      this.debounceTimer = setTimeout(() => this.runSearch(term), 300);
    },
    async runSearch(term) {
      this.searching = true;
      try {
        const res = await axios.post(
          CURR_API + "/tswebhook.php",
          {
            AccountUser: {
              API_Meth: "SearchAccountForEdit",
              accountnumber: this.allData.patient.externId2,
              searchTerm: term,
            },
          },
          { headers: { "Content-Type": "application/json;" } }
        );
        this.results = Array.isArray(res?.data?.records) ? res.data.records : [];
      } catch (e) {
        console.error("SearchAccountForEdit failed:", e);
        this.results = [];
      } finally {
        this.searching = false;
      }
    },
    pickResult(r) {
      this.selectedRecord = r;
      this.editChoiceMade = false;
      this.editKeepCurrentType = false;
    },
    /** Keep our own cached copy in sync after a successful save, so going back
     * to the search list (or reopening this same person) reflects current
     * values without needing a fresh network round trip. */
    onSaved(updatedRecord) {
      this.selectedRecord = updatedRecord;
      const idx = this.results.findIndex(
        (r) => (updatedRecord.uniqueID && r.uniqueID === updatedRecord.uniqueID) || r.email === updatedRecord.email
      );
      if (idx !== -1) {
        this.results.splice(idx, 1, updatedRecord);
      }
    },
    chooseEditDetails() {
      this.editKeepCurrentType = true;
      this.editChoiceMade = true;
    },
    chooseAddNewType() {
      this.editKeepCurrentType = false;
      this.editChoiceMade = true;
    },
    handleClose() {
      if (this.selectedRecord) {
        // Opened from Administration chooser with a preselected user — close
        // entirely rather than dropping back into this component's search UI.
        if (this.initialRecord) {
          this.$emit("close");
          return;
        }
        // Back out of the edit form / choice dialog to the search list rather
        // than closing the whole modal, unless nothing was selected yet.
        this.selectedRecord = null;
        this.editChoiceMade = false;
        return;
      }
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.eus-modal {
  /* Backdrop color + z-index from brand-ds (.eh-popup-overlay / --eh-popup-*). */
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  padding: 24px 12px;
}

.eus-card {
  /* Surface, border, radius from brand-ds (.eh-popup-panel / --eh-popup-*). */
  padding: 1.5rem;
  width: 100%;
  max-width: 480px;
}

.eus-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.eus-head h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.eus-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}

.eus-hint {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 1rem;
}

.eus-search-wrap {
  position: relative;
}

.eus-search-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
}

.eus-search-input:focus {
  outline: none;
  border-color: #5a7ab0;
  box-shadow: 0 0 0 3px rgba(90, 122, 176, 0.15);
}

.eus-search-suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
  max-height: 280px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 4px;
}

.eus-suggestion-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  padding: 8px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
}

.eus-suggestion-btn:hover {
  background: #f5f8ff;
}

.eus-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  background: #5a7ab0;
}

.eus-suggestion-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.eus-suggestion-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.eus-suggestion-sub {
  font-size: 11.5px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.eus-empty-sm {
  padding: 10px;
  font-size: 12.5px;
  color: #64748b;
  text-align: center;
}

.eus-choice-buttons {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
</style>
